<template>
  <div v-if="show" class="modal-shadow">
    <div class="saved_popup w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 mx-auto rounded-lg overflow-hidden bg-white">
      <div class="modal_header flex justify-between items-center px-6 py-4 border-b">
        <h3 class="font-bold">{{ modalTitle }}</h3>
        <button @click="close" class="p-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#6B7280" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <div class="modal_body px-6 py-4">
        <div class="mb-4">
          <p class="text-sm text-gray-600 mb-3">
            Setting {{ pointType }} point for: <strong>{{ currentLocation && currentLocation.name }}</strong>
          </p>
          
          <div class="search-wrapper relative">
            <input v-model="searchQuery" @input="handleSearch" type="text"
              class="w-full border rounded-md px-4 py-2 pr-10" placeholder="Search an address..." />
            <svg class="absolute right-3 top-1/2" style="transform: translateY(-50%);" width="18" height="18"
              viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18Z"
                stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M19 19L16 16" stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div v-if="searchQuery && suggestions.length" class="suggestions">
            <ul>
              <li v-for="(s, i) in suggestions" :key="i" @click="selectSuggestion(s)" class="suggestion-item">
                {{ s.address }}
              </li>
            </ul>
          </div>
        </div>

        <div class="map-block">
          <div ref="map" class="map"></div>
        </div>

        <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm text-gray-600 mb-1">Address</label>
            <input type="text" class="w-full border rounded-md px-3 py-2" :value="address" disabled>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">Coordinates</label>
            <input type="text" class="w-full border rounded-md px-3 py-2" 
              :value="markerPosition ? `${markerPosition.lat.toFixed(6)}, ${markerPosition.lng.toFixed(6)}` : ''" disabled>
          </div>
        </div>
      </div>

      <div class="btn_line flex justify-end w-full px-6 py-4 border-t modal_footer">
        <button @click="close" class="this_button py-3 px-4 border rounded mr-3">Cancel</button>
        <button @click="save" :disabled="!markerPosition"
          :class="!markerPosition ? 'text-white bg-gray-400 border-gray-500' : 'text_chameleon bg-green-custom hover:bg-gray-200 border-green-custom'"
          class="this_button py-3 px-4 border rounded">
          Save {{ pointType }} Point
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { infoMessage } from "@/services/messages";
import { searchAddressesForModalDebounced } from "@/services/addressSearch";
import { getUserLocation, UK_FALLBACK_CENTER } from "@/services/userLocation";

export default {
  name: 'EntryExitPointModal',
  data() {
    return {
      show: false,
      pointType: 'entry', // 'entry' or 'exit'
      currentLocation: null, // The location we're adding entry/exit point to
      platform: null,
      map: null,
      ui: null,
      behavior: null,
      geocoder: null,
      marker: null,
      markerPosition: null,
      address: '',
      searchQuery: '',
      suggestions: [],
      resizeHandler: null,
      userAt: null,
      mapZoom: 14,
    }
  },
  computed: {
    modalTitle() {
      return `Add ${this.pointType === 'entry' ? 'Entry' : 'Exit'} Point`;
    }
  },
  methods: {
    open(type, currentLocation, coordinates) {
      this.pointType = type;
      this.currentLocation = currentLocation;
      this.show = true;
      
      this.$nextTick(async () => {
        const location = await getUserLocation();
        this.userAt = { lat: location.lat, lng: location.lng };
        this.mapZoom = location.fromUser ? 14 : 6;
        await this.loadHereMapsAPI();
        await this.initMap();
        
        // If coordinates provided from right-click, place marker there
        if (coordinates && coordinates.lat && coordinates.lng) {
          this.placeOrMoveMarker(coordinates.lat, coordinates.lng);
        }
      });
    },
    close() {
      this.resetState();
      this.show = false;
    },
    resetState() {
      this.searchQuery = '';
      this.suggestions = [];
      this.address = '';
      this.markerPosition = null;
      
      // Remove resize handler
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
      
      if (this.map) {
        this.map.dispose();
        this.map = null;
      }
      this.marker = null;
    },
    async loadHereMapsAPI() {
      return new Promise((resolve, reject) => {
        if (window.H && window.H.ui) {
          return resolve();
        }
        const scripts = [
          'https://js.api.here.com/v3/3.2/mapsjs-core.js',
          'https://js.api.here.com/v3/3.2/mapsjs-service.js',
          'https://js.api.here.com/v3/3.2/mapsjs-ui.js',
          'https://js.api.here.com/v3/3.2/mapsjs-mapevents.js'
        ];
        let loadedCount = 0;
        scripts.forEach(src => {
          const script = document.createElement('script');
          script.src = src;
          script.defer = true;
          script.onload = () => {
            loadedCount++;
            if (loadedCount === scripts.length) {
              resolve();
            }
          };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      });
    },
    async initMap() {
      if (!window.H) {
        console.error('HERE Maps API not loaded');
        return;
      }

      this.platform = new H.service.Platform({
        apikey: process.env.VUE_APP_HERE_API_KEY,
      });
      this.geocoder = this.platform.getSearchService();
      const engineType = H.Map.EngineType.HARP;
      const defaultLayers = this.platform.createDefaultLayers({ engineType });
      
      // Center on current location if available
      const center = (this.currentLocation && this.currentLocation.geo)
        ? { lat: this.currentLocation.geo.latitude, lng: this.currentLocation.geo.longitude }
        : (this.userAt || UK_FALLBACK_CENTER);
      
      this.map = new H.Map(
        this.$refs.map,
        defaultLayers.vector.normal.map,
        {
          zoom: this.mapZoom,
          center: center,
          engineType
        }
      );

      this.behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map));
      this.ui = H.ui.UI.createDefault(this.map, defaultLayers);
      this.map.addLayer(defaultLayers.vector.traffic.logistics);
      
      // Store resize handler
      this.resizeHandler = () => {
        if (this.map) {
          this.map.getViewPort().resize();
        }
      };
      window.addEventListener('resize', this.resizeHandler);
    },
    placeOrMoveMarker(lat, lng) {
      const position = { lat, lng };
      if (!this.marker) {
        const iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42"><path fill="#1976d2" stroke="#fff" stroke-width="2" d="M15,40 C15,40 2,24 2,15 C2,8 8,2 15,2 C22,2 28,8 28,15 C28,24 15,40 15,40 Z" /></svg>`;
        const icon = new H.map.Icon('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(iconSvg), { anchor: { x: 15, y: 40 } });
        this.marker = new H.map.Marker(position, { icon });
        this.marker.draggable = true;
        this.marker.addEventListener('dragstart', () => {
          if (this.behavior) this.behavior.disable(H.mapevents.Behavior.DRAGGING);
        }, false);
        this.marker.addEventListener('drag', (ev) => {
          const p = ev.currentPointer;
          const newPos = this.map.screenToGeo(p.viewportX, p.viewportY);
          ev.target.setGeometry(newPos);
        }, false);
        this.marker.addEventListener('dragend', (e) => {
          if (this.behavior) this.behavior.enable(H.mapevents.Behavior.DRAGGING);
          const pos = e.target.getGeometry();
          this.markerPosition = { lat: pos.lat, lng: pos.lng };
          this.reverseGeocode(pos.lat, pos.lng);
        }, false);
        this.map.addObject(this.marker);
      } else {
        this.marker.setGeometry(position);
      }
      this.markerPosition = { lat, lng };
      this.map.getViewModel().setLookAtData({ position, zoom: 16 }, true);
      this.reverseGeocode(lat, lng);
    },
    reverseGeocode(lat, lng) {
      if (!this.geocoder) return;
      this.geocoder.reverseGeocode(
        { at: `${lat},${lng}` },
        (result) => {
          if (result.items && result.items.length) {
            this.address = result.items[0].address.label;
          }
        },
        (err) => {
          console.error('Reverse geocode error', err);
        }
      );
    },
    async handleSearch() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        this.suggestions = [];
        return;
      }
      const user = JSON.parse(localStorage.getItem('user')) || null;
      this.suggestions = await searchAddressesForModalDebounced(
        this.$http,
        this.searchQuery,
        user ? user.id : null,
        'entry-exit',
        this.userAt,
      );
    },
    selectSuggestion(s) {
      this.searchQuery = s.address;
      this.suggestions = [];
      this.placeOrMoveMarker(s.position.lat, s.position.lng);
    },
    async save() {
      if (!this.markerPosition || !this.currentLocation) return;

      try {
        // Prepare the point data
        const pointData = {
          address: this.address,
          coordinates: {
            latitude: this.markerPosition.lat,
            longitude: this.markerPosition.lng
          }
        };

        // Get existing entry/exit data
        let existingData = this.currentLocation[this.pointType];
        if (typeof existingData === 'string') {
          try {
            existingData = JSON.parse(existingData);
          } catch (e) {
            existingData = null;
          }
        }

        // Create array if not exists
        if (!Array.isArray(existingData)) {
          existingData = [];
        }

        // Add new point
        existingData.push(pointData);

        // Prepare update payload
        const updateData = {
          [this.pointType]: existingData
        };

        // Send update request
        const response = await this.$http.putAuth(
          `${this.$http.apiUrl()}/locations/${this.currentLocation.id}/update`,
          updateData
        );

        if (response && response.data && response.data.statusCode === 200) {
          infoMessage(
            `${this.pointType === 'entry' ? 'Entry' : 'Exit'} point added successfully`,
            'success'
          );
          this.$emit('updated', this.currentLocation.id);
          this.close();
        } else {
          throw new Error('Update failed');
        }
      } catch (error) {
        console.error('Error saving point:', error);
        infoMessage(
          `Failed to add ${this.pointType} point: ${error.message || 'Unknown error'}`,
          'error'
        );
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-shadow {
  position: fixed;
  top: 0;
  left: 0;
  min-height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.39);
  z-index: 1000;
}

.modal_header {
  border-radius: 8px 8px 0 0;
}

.modal_footer {
  border-radius: 0 0 8px 8px;
}

.map-block {
  height: 50vh;
}

.map {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.suggestions {
  border: 1px solid #D0D5DA;
  border-radius: 6px;
  margin-top: 6px;
  max-height: 240px;
  overflow-y: auto;
}

.suggestions ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestion-item {
  padding: 10px 12px;
  cursor: pointer;
}

.suggestion-item:hover {
  background: #f5f5f5;
}

.this_button {
  font-weight: 600;
  transition: all 0.2s ease;
}

.text_chameleon {
  color: #1FBC2F;
}

.bg-green-custom {
  background-color: #E8F5E9;
}

.border-green-custom {
  border-color: #1FBC2F;
}
</style>

